import { Injectable } from '@angular/core';

import { AzavistaApiService, IEvent } from '@azavista/servicelib';
import { ChartType } from './apextypes';
import { SalesByProductQuantityData } from './sales-by-product-quantity/interfaces';
import { objectKeys } from '@azavista/workflow-builder/workflow-builder';
import { SharedService } from '../shared.service';
import { TRANSLATION_MAP, TranslationMap, TranslationMapKey } from '../const';


export type GroupedBarSeriesData = {
    [labelName: string]: {
        [seriesDataName: string]: number
    }
}

const seriesDataNameDataNameTranslations = {
    max_units: TRANSLATION_MAP.REPORT_GROUP_MAX_UNITS,
    reserved: TRANSLATION_MAP.REPORT_GROUP_RESERVED,
    sold: TRANSLATION_MAP.REPORT_GROUP_SOLD,
} satisfies Record<keyof (SalesByProductQuantityData[string]), TranslationMapKey>;

@Injectable({
    providedIn: 'root'
})
export class WidgetsUtilsService {
    constructor(
        private readonly apiSvc: AzavistaApiService,
        private sharedSvc: SharedService,
    ) { }


    areRemoteSettingsEmpty(remoteSettings: any): boolean {
        if (!remoteSettings || Object.keys(remoteSettings).length === 0) {
            return true;
        }
        return false;
    }

    getViewDetailsNavQueryParamsForEvent(eventId: string) {
        return { eventId: eventId, type: 'event' };
    }

    convertSeries(chartType: ChartType, series: number[]): ApexAxisChartSeries | ApexNonAxisChartSeries {
        if (!series) {
            series = [];
        }
        if (chartType === 'bar') {
            // Bar requires series in format { data: number[] }[]
            return [{ data: series }];
        } else if (chartType === 'donut') {
            // Donut requires series in format number[]
            return series;
        } else {
            return series;
        }
    }

    convertGroupedBarSeries<T extends GroupedBarSeriesData>(series: T): ApexAxisChartSeries {
        if (!series) {
            return [];
        }
        const seriesDataNames = objectKeys(objectKeys(series).reduce((record, key) => {
            const item = series[key];
            objectKeys(item).forEach((itemKey) => {
                record[String(itemKey)] = true;
            });
            return record;
        }, {} as Record<string, true>));
        return seriesDataNames.map(seriesName => ({
            name: this.sharedSvc.translate((seriesDataNameDataNameTranslations as any)[seriesName] ?? seriesName),
            data: objectKeys(series).map(seriesKey => series[seriesKey][seriesName] ?? 0)
        }));
    }

    async getEvent(eventId: string): Promise<IEvent> {
        if (!eventId) {
            return null;
        }
        try {
            return await this.apiSvc.getEvent(eventId);
        } catch (err) {
            return null;
        }
    }

    convertDataToSeriesAndLabels(data: { [key: string]: number }): { series: number[], labels: string[] } {
        if (!data) {
            return {
                labels: [],
                series: []
            };
        }
        const keys = Object.keys(data);
        const series: number[] = [];
        const labels: string[] = [];
        for (const key of keys) {
            series.push(data[key]);
            labels.push(key === '' ? `${this.sharedSvc.translate(TRANSLATION_MAP.EMPTY_VALUE)}` : key);
        }

        return {
            series: series,
            labels: labels
        };
    }
}
